import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CurveDivider } from '../container/PageHeader/PageHeader';
import PurePralinePerfectionBranding from '@/res/imgs/ppp_src.webp';

export interface HomeHeaderProps {
  className?: string;
}

function HomeHeader({ className = '' }: HomeHeaderProps) {
  // ============================================================================
  // COMMERCIAL PATTERN: Dynamic Asset Preloading
  // Forces the browser's preload scanner to fetch the branding image immediately,
  // bypassing the standard React render lifecycle to eliminate LCP flicker.
  // ============================================================================
  useEffect(() => {
    const existingLink = document.querySelector(
      `link[href="${PurePralinePerfectionBranding}"]`,
    );
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = PurePralinePerfectionBranding;
      // Depending on your server config, you might also add link.fetchPriority = 'high';
      document.head.appendChild(link);
    }
  }, []);

  // Framer Motion Orchestration
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 },
    },
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 70, damping: 15 },
    },
  } as const;

  return (
    <section
      className={`relative bg-brand-gold text-brand-cream min-h-[75vh] md:min-h-[85vh] flex flex-col justify-center overflow-hidden ${className}`}
    >
      <div className="container mx-auto px-6 md:px-10 relative z-10 flex flex-col-reverse md:flex-row items-center justify-between gap-12 md:gap-8 pt-24 pb-32">
        {/* ============================================================================ */}
        {/* TEXT CONTENT: Polarizing, Confident, & Unapologetic                          */}
        {/* ============================================================================ */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="flex-1 flex flex-col gap-6 max-w-2xl text-center md:text-left z-20"
        >
          <motion.p
            variants={itemVariants}
            className="font-display font-bold text-sm tracking-[0.3em] uppercase text-brand-sage drop-shadow-sm"
          >
            Mandatory Indulgence
          </motion.p>

          <motion.h1
            variants={itemVariants}
            className="font-display font-[900] text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-brand-cream leading-[1.05] tracking-tight drop-shadow-md"
          >
            Ruin Ordinary <br className="hidden md:block" /> Sweets{' '}
            <span className="italic text-brand-sage font-[400]">Forever.</span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="font-body text-brand-cream/90 font-[400] text-lg md:text-xl leading-relaxed max-w-lg mx-auto md:mx-0"
          >
            We didn't invent the praline. We just perfected it to the point of
            absolute obsession. Hand-crafted, unapologetically rich, and
            dangerously addictive. Fair warning: your tastebuds will never
            settle for "average" again.
          </motion.p>

          {/* Optional CTA to scroll down to the dynamically rendered HomeBoxes */}
          <motion.div variants={itemVariants} className="pt-4">
            <button
              onClick={() =>
                document
                  .getElementById('page-cn-section')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
              className="px-8 py-4 rounded-full bg-text-muted text-brand-cream font-display font-[500] text-sm tracking-widest uppercase transition-all hover:scale-105 hover:shadow-xl active:scale-95"
            >
              <span className="translate-y-[1px]">Satisfy the Craving</span>
            </button>
          </motion.div>
        </motion.div>

        {/* ============================================================================ */}
        {/* BADGE / LOGO CONTAINER: Preloaded, Eager, and Animated                       */}
        {/* ============================================================================ */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, rotate: -15 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="flex-1 w-full flex items-center justify-center relative max-w-[280px] md:max-w-[450px] mx-auto"
        >
          {/* Subtle ambient glow behind the logo to make it pop against the brand-frost background */}
          <div className="absolute inset-0 bg-brand-sage/20 blur-[100px] rounded-full scale-150 animate-pulse pointer-events-none" />

          {/* Continuous slow rotation to draw the eye, establishing the badge as a premium seal */}
          <motion.img
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
            src={PurePralinePerfectionBranding}
            alt="Pure Praline Perfection Seal"
            className="w-full h-auto object-contain relative z-10 drop-shadow-2xl"
            fetchPriority="high"
            loading="eager"
          />
        </motion.div>
      </div>

      {/* Re-integrated CurveDivider[cite: 6], translating it down 1px to prevent sub-pixel background bleed */}
      <CurveDivider className="!text-brand-cream absolute bottom-0 left-0 w-full translate-y-[1px] z-30" />
    </section>
  );
}

HomeHeader.variant = 'hero';

export default HomeHeader;
